import { CURRENCY_INFO, type CurrencyCode, type KhqrCurrency, type Language } from '@expenditure/core';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, useCurrentUser } from '../auth/AuthProvider';
import { Switch } from '../components/Sheet';
import { useToast } from '../components/Toast';
import { Avatar, PageHeader, Segmented } from '../components/ui';
import { api } from '../lib/api';
import { errorMessage } from '../lib/format';
import { decodeQrFromImage } from '../lib/qr-scan';
import { confirmDialog, haptic, openPage, requestBotMessages } from '../lib/telegram';
import type { User } from '../lib/types';
import { GuideButton, useGuide } from '../components/Guide';
import { Icon } from '../components/Icon';
import { PlanCard } from '../components/Pro';
import { SupportCard } from '../components/KhqrPay';
import { usePlan } from '../lib/queries';

export function ProfilePage() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { setUser } = useAuth();
  const toast = useToast();
  const plan = usePlan();
  const guide = useGuide();

  const save = async (action: () => Promise<{ user: User }>, success?: string) => {
    try {
      setUser((await action()).user);
      if (success) toast(success);
      return true;
    } catch (error) {
      toast(errorMessage(error, t), 'error');
      return false;
    }
  };

  const update = (prefs: Parameters<typeof api.updateMe>[0]) => {
    haptic.select();
    return save(() => api.updateMe(prefs));
  };

  const enableNotifications = async () => {
    const granted = await requestBotMessages();
    if (granted) await update({ botAllowed: true });
    else toast(t('profile.permissionDenied'), 'error');
  };

  return (
    <main className="page app">
      <PageHeader title={t('profile.title')} />

      <div className="card row">
        <Avatar name={user.fullName} photo={user.profilePhoto} />
        <div className="grow">
          <div className="list-title">{user.fullName}</div>
          {user.username && <div className="list-sub">@{user.username}</div>}
        </div>
      </div>

      <PlanCard />
      {plan.data?.khqr?.tips && <SupportCard />}

      <PaymentDetails user={user} onSave={save} />

      <div className="card stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div className="grow">
            <div className="list-title">{t('profile.notifications')}</div>
            <div className="muted small">{t('profile.notificationsHint')}</div>
          </div>
          {user.botAllowed ? (
            <span className="badge badge--pos">{t('profile.notificationsOn')}</span>
          ) : (
            <button className="btn btn--soft btn--small" onClick={() => void enableNotifications()}>
              {t('profile.enableNotifications')}
            </button>
          )}
        </div>
        {user.botAllowed && (
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span>{t('profile.expenseAlerts')}</span>
            <Switch
              checked={user.notifyExpenses}
              label={t('profile.expenseAlerts')}
              onChange={(notifyExpenses) => void update({ notifyExpenses })}
            />
          </div>
        )}
        {user.botAllowed && (
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div className="grow">
              <div>{t('profile.weeklyReminders')}</div>
              <div className="muted small">{t('profile.weeklyRemindersHint')}</div>
            </div>
            <Switch
              checked={user.notifyReminders}
              label={t('profile.weeklyReminders')}
              onChange={(notifyReminders) => void update({ notifyReminders })}
            />
          </div>
        )}
      </div>

      <div className="card stack">
        <div className="field">
          <span className="field-label">{t('profile.language')}</span>
          <Segmented<Language>
            value={user.language}
            onChange={(language) => void update({ language })}
            options={[
              { value: 'km', label: 'ខ្មែរ' },
              { value: 'en', label: 'English' },
            ]}
          />
        </div>
        <div className="field">
          <span className="field-label">{t('profile.defaultCurrency')}</span>
          <Segmented<KhqrCurrency>
            value={user.defaultCurrency}
            onChange={(defaultCurrency) => void update({ defaultCurrency })}
            options={[
              { value: 'USD', label: 'USD $' },
              { value: 'KHR', label: 'KHR ៛' },
            ]}
          />
          <span className="field-hint">{t('profile.defaultCurrencyHint')}</span>
        </div>
      </div>

      <div className="card stack" style={{ gap: 4 }}>
        <div className="list-title">{t('profile.about')}</div>
        <div className="muted small">
          {t('app.name')} — {t('app.tagline')}
        </div>
        <div className="muted small">{t('profile.version', { version: __APP_VERSION__ })}</div>
        <div style={{ marginTop: 8 }}>
          <GuideButton onClick={guide.show} />
          {guide.sheet}
        </div>
        <div className="legal-links small">
          {(['privacy', 'terms'] as const).map((page) => (
            <a
              key={page}
              href={`/${page}`}
              onClick={(event) => {
                event.preventDefault();
                openPage(`/${page}`);
              }}
            >
              {t(`profile.${page}`)}
            </a>
          ))}
        </div>
      </div>

      <DeleteAccount />
    </main>
  );
}

/** Deletes the account for good (see the API's account.service.ts for what happens to shared groups). */
function DeleteAccount() {
  const { t } = useTranslation();
  const { accountDeleted } = useAuth();
  const toast = useToast();
  const [deleting, setDeleting] = useState(false);

  const remove = async () => {
    haptic.warning();
    if (!(await confirmDialog(t('profile.deleteConfirm')))) return;
    setDeleting(true);
    try {
      await api.deleteAccount();
      haptic.success();
      accountDeleted();
    } catch (error) {
      setDeleting(false);
      toast(errorMessage(error, t), 'error');
    }
  };

  return (
    <div className="card stack">
      <div>
        <div className="list-title">{t('profile.deleteTitle')}</div>
        <div className="muted small">{t('profile.deleteHint')}</div>
      </div>
      <button className="btn btn--danger btn--block" disabled={deleting} onClick={() => void remove()}>
        <Icon name={deleting ? 'loader' : 'trash'} size={18} className={deleting ? 'spin' : undefined} />
        {t('profile.deleteButton')}
      </button>
    </div>
  );
}

/** Upload (or paste) the KHQR from your bank app so others can pay you by scanning. */
function PaymentDetails({ user, onSave }: { user: User; onSave: (action: () => Promise<{ user: User }>, success?: string) => Promise<boolean> }) {
  const { t } = useTranslation();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (payload: string) => {
    setBusy(true);
    if (await onSave(() => api.setKhqr(payload), t('profile.qrSaved'))) setPasted('');
    setBusy(false);
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const payload = await decodeQrFromImage(file);
      if (!payload) {
        toast(t('profile.qrNotFound'), 'error');
        return;
      }
      await submit(payload);
    } catch {
      toast(t('profile.qrNotFound'), 'error');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <div className="card stack">
      <div>
        <div className="list-title">{t('profile.payment')}</div>
        <div className="muted small">{t('profile.paymentHint')}</div>
      </div>

      {user.khqr && (
        <div className="khqr-account">
          <span className="khqr-badge" aria-hidden>
            KHQR
          </span>
          <div className="grow">
            <div className="list-title ellipsis">{user.khqr.name}</div>
            <div className="list-sub ellipsis">
              {user.khqr.accountId} · {CURRENCY_INFO[user.khqr.currency].code}
            </div>
          </div>
          <button className="btn btn--ghost btn--small" style={{ color: 'var(--negative)' }} onClick={() => void onSave(() => api.removeKhqr())}>
            {t('profile.removeQr')}
          </button>
        </div>
      )}

      <input ref={fileInput} type="file" accept="image/*" hidden onChange={(e) => void onFile(e.target.files?.[0])} />
      <button className="btn btn--soft" disabled={busy} onClick={() => fileInput.current?.click()}>
        <Icon name="qr" size={18} />
        {t('profile.uploadQr')}
      </button>
      <details>
        <summary className="muted small" style={{ cursor: 'pointer' }}>
          {t('profile.pasteQr')}
        </summary>
        <div className="row" style={{ marginTop: 8 }}>
          <input
            className="input grow num small"
            value={pasted}
            placeholder={t('profile.pastePlaceholder')}
            onChange={(e) => setPasted(e.target.value.trim())}
          />
          <button className="btn btn--soft" disabled={busy || pasted.length < 20} onClick={() => void submit(pasted)}>
            {t('profile.saveQr')}
          </button>
        </div>
      </details>
    </div>
  );
}
